import React, { useCallback, useContext, useEffect, useState } from 'react';

import { CombatContext } from '../contexts/CombatContext';
import { ActionPill, StatusBadge, useLayeredModal } from './ui/LayeredWorkspace';
import { runtimeContextPayload } from './combatContextIdentity';
import {
  archivePrivateMessage, ensureSharingContext, fetchDmPrivateMessages,
  fetchPlayerPrivateMessages, fetchPrivateMessageOverview, markPrivateMessageRead,
  resolvePrivateMessage, respondToPrivateMessage, sendPrivateCampaignMessage,
} from './sharingApi';

const MESSAGE_TYPES = ['INFORMATION', 'QUESTION', 'DECISION', 'PRIVATE_REVEAL'];
const PERSISTENCE_LEVELS = ['TEMPORARY_CURRENT', 'CAMPAIGN', 'RESOLVED_HISTORY'];

function MessageCard({ message, mode, onRefresh }) {
  const [freeText, setFreeText] = useState('');
  const [notice, setNotice] = useState('');
  const states = message.states || [];

  async function read() {
    if (mode !== 'player' || states.indexOf('UNREAD') < 0) return;
    await markPrivateMessageRead(message.id);
    onRefresh();
  }

  async function respond(option) {
    try {
      await respondToPrivateMessage(message.id, {
        expected_version: message.version,
        selected_option: option || undefined,
        free_text: freeText.trim() || undefined,
      });
      setFreeText(''); setNotice('Response sent privately to the DM.'); onRefresh();
    } catch (error) { setNotice('The response was rejected or this card changed.'); }
  }

  async function transition(kind) {
    try {
      if (kind === 'resolve') await resolvePrivateMessage(message.id, message.version);
      else await archivePrivateMessage(message.id, message.version);
      onRefresh();
    } catch (error) { setNotice('The message changed. Refresh and try again.'); }
  }

  return (
    <article className="cp-private-message-card" onFocus={read} onMouseEnter={read}>
      <div className="cp-private-message-head"><div><span>{message.message_type.replace(/_/g, ' ')}</span><strong>{message.title}</strong></div><div className="cp-private-message-states">{states.map(function (state) { return <StatusBadge key={state} tone={state.toLowerCase()}>{state.replace(/_/g, ' ')}</StatusBadge>; })}</div></div>
      {mode === 'dm' ? <small>Recipient: {message.recipient_display_name}</small> : null}
      <p>{message.content}</p>
      {message.safe_attachment ? <div className="cp-private-reveal"><b>Private reveal</b>{(message.safe_attachment.player_safe_facts || []).map(function (fact, index) { return <span key={index}>{typeof fact === 'string' ? fact : JSON.stringify(fact)}</span>; })}</div> : null}
      {message.response && (message.response.selected_option || message.response.free_text) ? <div className="cp-private-response"><b>Private response</b><span>{message.response.selected_option || message.response.free_text}</span></div> : null}
      {mode === 'player' && message.status === 'PENDING_RESPONSE' ? <div className="cp-private-response-controls">
        {(message.response_options || []).map(function (option) { return <ActionPill key={option} onClick={function () { respond(option); }}>{option}</ActionPill>; })}
        {message.allow_free_text ? <><label>Short response<textarea maxLength="1000" value={freeText} onChange={function (event) { setFreeText(event.target.value); }} /></label><ActionPill primary disabled={!freeText.trim()} onClick={function () { respond(null); }}>Send Response</ActionPill></> : null}
      </div> : null}
      {mode === 'dm' && message.status !== 'ARCHIVED' ? <div className="cp-action-row">
        {['READ', 'PENDING_RESPONSE', 'RESPONDED'].indexOf(message.status) >= 0 ? <ActionPill onClick={function () { transition('resolve'); }}>Resolve</ActionPill> : null}
        <ActionPill onClick={function () { transition('archive'); }}>Archive</ActionPill>
      </div> : null}
      <small>{message.persistence_level.replace(/_/g, ' ')}{message.bindings && message.bindings.encounter_id ? ' · Bound to encounter' : ''}</small>
      {notice ? <p role="status">{notice}</p> : null}
    </article>
  );
}

export function DmPrivateMessagesWorkspace() {
  const { state } = useContext(CombatContext);
  const [context, setContext] = useState(null);
  const [messages, setMessages] = useState([]);
  const [overview, setOverview] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [type, setType] = useState('INFORMATION');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [options, setOptions] = useState('Tell everyone\nTell selected player\nKeep secret');
  const [allowFreeText, setAllowFreeText] = useState(false);
  const [persistence, setPersistence] = useState('CAMPAIGN');
  const [sourceResultId, setSourceResultId] = useState('');
  const [sourceCardId, setSourceCardId] = useState('');
  const [notice, setNotice] = useState('Connecting private campaign messages…');

  async function refresh(identity) {
    const active = identity || context;
    if (!active) return;
    const results = await Promise.all([fetchDmPrivateMessages(active.campaign_id), fetchPrivateMessageOverview(active.campaign_id)]);
    setMessages(results[0].messages || []); setOverview(results[1].players || []);
  }

  useEffect(function () {
    let live = true;
    ensureSharingContext(runtimeContextPayload(state)).then(function (identity) {
      if (!live) return;
      setContext(identity); setNotice('Private campaign message cards ready.');
      return refresh(identity);
    }).catch(function () { if (live) setNotice('Private campaign messages could not connect.'); });
    return function () { live = false; };
    // Identity is established once when the 95% workspace opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function toggleRecipient(playerId) {
    setRecipients(function (current) { return current.indexOf(playerId) >= 0 ? current.filter(function (id) { return id !== playerId; }) : current.concat([playerId]); });
  }

  async function send() {
    if (!context || !recipients.length || !title.trim() || !content.trim()) return;
    try {
      await sendPrivateCampaignMessage({
        campaign_id: context.campaign_id, recipient_player_ids: recipients,
        title: title.trim(), message_type: type, content: content.trim(),
        response_options: type === 'DECISION' || type === 'QUESTION' ? options.split('\n').map(function (value) { return value.trim(); }).filter(Boolean) : [],
        allow_free_text: (type === 'DECISION' || type === 'QUESTION') && allowFreeText,
        persistence_level: persistence, encounter_id: context.encounter_id || null,
        source_result_id: sourceResultId.trim() || null,
        source_information_card_id: sourceCardId.trim() || null,
        source_fact_index: 0,
      });
      setTitle(''); setContent(''); setRecipients([]); setNotice('Private card sent to the selected player accounts.');
      await refresh(context);
    } catch (error) { setNotice('Send rejected. Check recipients, source binding and required decision options.'); }
  }

  return <div className="cp-private-messages">
    <p className="cp-visibility-notice" role="status">{notice}</p>
    {context ? <div className="cp-private-player-entry"><b>Player DM Messages entry</b><code>{'/combat-pro/campaign/' + context.campaign_id + '/messages'}</code></div> : null}
    <section className="cp-private-overview" aria-label="Private message overview">{overview.map(function (row) { return <div key={row.player_id}><strong>{row.display_name}</strong><span>{row.unread} unread</span><span>{row.pending_questions} pending question</span><span>{row.responses_received} response received</span><span>{row.unresolved_decisions} unresolved decision</span></div>; })}</section>
    <section className="cp-share-compose" aria-label="Compose structured private campaign card"><h3>New private campaign card</h3>
      <div className="cp-private-recipients">{((context && context.players) || []).map(function (player) { return <ActionPill key={player.player_id} primary={recipients.indexOf(player.player_id) >= 0} onClick={function () { toggleRecipient(player.player_id); }}>{player.display_name}</ActionPill>; })}</div>
      <div className="cp-share-grid">
        <label>Type<select value={type} onChange={function (event) { setType(event.target.value); }}>{MESSAGE_TYPES.map(function (value) { return <option key={value}>{value}</option>; })}</select></label>
        <label>Persistence<select value={persistence} onChange={function (event) { setPersistence(event.target.value); }}>{PERSISTENCE_LEVELS.map(function (value) { return <option key={value}>{value}</option>; })}</select></label>
        <label>Title<input maxLength="240" value={title} onChange={function (event) { setTitle(event.target.value); }} /></label>
        <label>Campaign information<textarea maxLength="4000" value={content} onChange={function (event) { setContent(event.target.value); }} /></label>
        {(type === 'QUESTION' || type === 'DECISION') ? <label>Authorised response choices<textarea value={options} onChange={function (event) { setOptions(event.target.value); }} /></label> : null}
        {(type === 'QUESTION' || type === 'DECISION') ? <label className="cp-private-check"><input type="checkbox" checked={allowFreeText} onChange={function (event) { setAllowFreeText(event.target.checked); }} /> Allow one short free-text response</label> : null}
        {type === 'PRIVATE_REVEAL' ? <><label>Source information card ID<input value={sourceCardId} onChange={function (event) { setSourceCardId(event.target.value); }} /></label><label>Source generator result ID<input value={sourceResultId} onChange={function (event) { setSourceResultId(event.target.value); }} /></label></> : null}
      </div><div className="cp-action-row"><ActionPill primary disabled={!recipients.length || !title.trim() || !content.trim()} onClick={send}>Send Private Card</ActionPill></div>
    </section>
    <section aria-label="Private message history"><h3>Campaign history</h3><div className="cp-private-message-list">{messages.map(function (message) { return <MessageCard key={message.id} message={message} mode="dm" onRefresh={function () { refresh(context); }} />; })}</div></section>
  </div>;
}

export function PlayerDmMessagesWorkspace({ campaignId }) {
  const [messages, setMessages] = useState([]);
  const [notice, setNotice] = useState('Loading DM message cards…');
  const refresh = useCallback(async function () {
    try { const result = await fetchPlayerPrivateMessages(campaignId); setMessages(result.messages || []); setNotice(''); }
    catch (error) { setNotice('DM messages could not be loaded for this campaign.'); }
  }, [campaignId]);
  useEffect(function () { refresh(); }, [refresh]);
  return <div className="cp-private-messages"><p role="status">{notice}</p><div className="cp-private-message-list">{messages.map(function (message) { return <MessageCard key={message.id} message={message} mode="player" onRefresh={refresh} />; })}</div></div>;
}

export function PrivateMessagesChip() {
  const { openLayer } = useLayeredModal();
  return <button type="button" className="cp-workspace-chip" onClick={function () { openLayer({ title: 'Private Messages', eyebrow: 'DM Campaign Cards', subtitle: 'Purposeful private information, questions and decisions. This is not chat.', content: <DmPrivateMessagesWorkspace /> }); }}>Private Messages</button>;
}

export function DmMessagesChip({ campaignId }) {
  const { openLayer } = useLayeredModal();
  return <button type="button" className="cp-workspace-chip" onClick={function () { openLayer({ title: 'DM Messages', eyebrow: 'Private Campaign Cards', subtitle: 'Information, questions, decisions and private reveals sent by your DM.', content: <PlayerDmMessagesWorkspace campaignId={campaignId} /> }); }}>DM Messages</button>;
}

export default PrivateMessagesChip;
